<script setup lang="ts">
import type { PanelTab } from '../../../lib/brandAppSections'
import PanelGroup from '../../PanelGroup.vue'
import { ArrowUpRight, Loader2 } from 'lucide-vue-next'
import useOffer from '@/composables/offer'
import type { Offer } from '@/types/types'
import { isContentVisible, type BrandAppOfferCard } from '../../../lib/brandAppContract'
import type { CardListItem } from './CardList.vue'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import CardList from './CardList.vue'
import BlockTitleField from './BlockTitleField.vue'
import OfferEditor from './OfferEditor.vue'
import useContentEditing from '../../../composables/useContentEditing'
import { isBrandAppOffer, offerCardErrors } from '../../../lib/brandAppOffers'

defineProps<{ tab?: PanelTab }>()

const { t } = useI18n()
const { fetchOffers } = useOffer()
const { home, setHomeOfferIds, saveHomeOffer, removeHomeOffer } = useBrandAppConfig()
const offers = ref<Offer[]>([])
const isLoading = ref(true)
const loadFailed = ref(false)
const { editing, startEditing, stopEditing } = useContentEditing()
const editorOpen = computed(() => editing.value?.kind === 'offer')
const editingCard = ref<BrandAppOfferCard>()
const pendingDelete = ref<number>()
const availableOffers = computed(() => offers.value.filter((offer) => !home.value.offerIds.includes(offer.id)))
const editorOffers = computed(() =>
  editingCard.value ? offers.value.filter((offer) => offer.id === editingCard.value?.offerId) : availableOffers.value
)
const offersById = computed(() => Object.fromEntries(offers.value.map((offer) => [offer.id, offer])))
const offerItems = computed(() =>
  home.value.offerIds.map((id) => ({
    id: String(id),
    label:
      home.value.offerCards[id]?.title ??
      offersById.value[id]?.name ??
      t('pages.loyaltySupports.webapp.offers.unavailable')
  }))
)
const deleteTitle = computed(
  () => offerItems.value.find((item) => Number(item.id) === pendingDelete.value)?.label ?? ''
)

const load = async () => {
  isLoading.value = true
  loadFailed.value = false
  try {
    offers.value = (await fetchOffers()).filter(isBrandAppOffer)
  } catch {
    loadFailed.value = true
  } finally {
    isLoading.value = false
  }
}
onMounted(load)
onBeforeUnmount(() => stopEditing('offer'))

const closeEditor = () => {
  stopEditing('offer')
  editingCard.value = undefined
}

const edit = (id?: number) => {
  editingCard.value = id === undefined ? undefined : home.value.offerCards[id]
  if (id !== undefined && !editingCard.value) return
  startEditing('offer', id === undefined)
}
const save = (card: BrandAppOfferCard) => {
  if (!editorOffers.value.some((offer) => offer.id === card.offerId)) return
  saveHomeOffer(card)
  closeEditor()
}
const setVisible = (id: string, isVisible: boolean) => {
  const card = home.value.offerCards[Number(id)]
  if (!card) return
  if (isVisible && Object.keys(offerCardErrors(card)).length > 0) {
    edit(Number(id))
    return
  }
  saveHomeOffer({ ...card, isVisible })
}
const remove = () => {
  if (pendingDelete.value === undefined) return
  removeHomeOffer(pendingDelete.value)
  pendingDelete.value = undefined
}
const reorder = (next: string[]) => {
  const ids = next.map(Number)
  if (
    ids.length !== home.value.offerIds.length ||
    new Set(ids).size !== ids.length ||
    ids.some((id) => !home.value.offerIds.includes(id))
  )
    return
  setHomeOfferIds(ids)
}

const cardItems = computed<CardListItem[]>(() =>
  home.value.offerIds.map((id) => {
    const card = home.value.offerCards[id]
    const offer = offersById.value[id]

    return {
      id: String(id),
      title: card?.title ?? offer?.name ?? '',
      subtitle: offer?.name,
      image: card?.image,
      hidden: card ? !isContentVisible(card) : undefined,
      warning: offer ? undefined : t('pages.loyaltySupports.webapp.offers.unavailable')
    }
  })
)
</script>

<template>
  <OfferEditor
    v-if="editorOpen"
    class="motion-safe:animate-panel-forward"
    :offers="editorOffers"
    :card="editingCard"
    @save="save"
    @close="closeEditor"
  />

  <PanelGroup v-else-if="tab !== 'content'">
    <BlockTitleField block-id="offers" />
  </PanelGroup>

  <PanelGroup
    v-if="!editorOpen && tab !== 'text'"
    :title="t('pages.loyaltySupports.webapp.offers.list')"
    :count="home.offerIds.length"
  >
    <div class="flex flex-col gap-4">
      <div v-if="isLoading" role="status" class="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 class="size-4 animate-spin" />{{ t('pages.loyaltySupports.webapp.offers.loading') }}
      </div>
      <div v-else-if="loadFailed" class="flex flex-col items-start gap-2">
        <p role="alert" class="text-destructive text-sm">{{ t('pages.loyaltySupports.webapp.offers.loadFailed') }}</p>
        <Button variant="outline" size="sm" @click="load">{{ t('pages.loyaltySupports.webapp.offers.retry') }}</Button>
      </div>
      <template v-else>
        <div v-if="!offers.length" class="flex flex-col items-start gap-2">
          <p class="text-muted-foreground text-sm">
            {{ t('pages.loyaltySupports.webapp.offers.noOffers') }}
          </p>
          <Button variant="link" class="px-0" as-child>
            <RouterLink :to="{ name: 'OfferList' }">
              {{ t('pages.loyaltySupports.webapp.offers.create') }}<ArrowUpRight />
            </RouterLink>
          </Button>
        </div>
        <template v-else>
          <CardList
            :items="cardItems"
            :add-label="t('pages.loyaltySupports.webapp.offers.add')"
            :can-add="Boolean(availableOffers.length)"
            @update:order="reorder"
            @update:visible="setVisible"
            @edit="edit(Number($event))"
            @remove="pendingDelete = Number($event)"
            @add="edit()"
          />
          <p v-if="!availableOffers.length" class="text-muted-foreground text-xs">
            {{ t('pages.loyaltySupports.webapp.offers.allAdded') }}
          </p>
        </template>
      </template>
    </div>
  </PanelGroup>

  <BaseModal
    :is-open="pendingDelete !== undefined"
    size="sm"
    :heading-title="t('pages.loyaltySupports.webapp.offers.remove')"
    @close="pendingDelete = undefined"
  >
    <p class="text-sm">{{ t('pages.loyaltySupports.webapp.offers.removeConfirm', { title: deleteTitle }) }}</p>
    <template #linkBtn>
      <Button variant="ghost" @click="pendingDelete = undefined">
        {{ t('pages.loyaltySupports.webapp.offers.cancel') }}
      </Button>
      <Button variant="destructive" @click="remove">{{ t('pages.loyaltySupports.webapp.offers.remove') }}</Button>
    </template>
  </BaseModal>
</template>
